import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { ArrowRight, Sparkles } from 'lucide-react';

const Hero = () => {
  const { t } = useTranslation();
  const [videoFading, setVideoFading] = useState(false);
  const [videoHidden, setVideoHidden] = useState(false);
  const [videoFadingIn, setVideoFadingIn] = useState(true);
  const [showRecruiters, setShowRecruiters] = useState(true);
  const [textFading, setTextFading] = useState(false);
  const videoRef = React.useRef(null);

  // Alternate text every 5 seconds with fade effect and 0.5s pause
  useEffect(() => {
    const textInterval = setInterval(() => {
      // Start fade out
      setTextFading(true);
      
      // After fade out completes (300ms), wait 500ms, then switch text and fade in
      setTimeout(() => {
        setShowRecruiters(prev => !prev);
        setTextFading(false);
      }, 800); // 300ms fade out + 500ms pause
    }, 5000);

    return () => clearInterval(textInterval);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const currentTime = video.currentTime;
      const timeRemaining = video.duration - currentTime;
      
      // Hidden for first 0.25 seconds
      if (currentTime < 0.25) {
        setVideoFadingIn(false);
        setVideoFading(false);
        setVideoHidden(true);
      } else if (currentTime >= 0.25 && currentTime <= 3.25) {
        // Fade in from 0.25 to 3.25 seconds
        setVideoFadingIn(true);
        setVideoFading(false);
        setVideoHidden(false);
      } else if (timeRemaining <= 2 && timeRemaining > 0) {
        // Start fade out at 2 seconds remaining - completes at 0 seconds (video end)
        setVideoFadingIn(false);
        setVideoFading(true);
        setVideoHidden(false);
      } else {
        // Normal playback
        setVideoFadingIn(false);
        setVideoFading(false);
        setVideoHidden(false);
      }
    };

    const handleEnded = () => {
      // Ensure video is at complete opacity 0
      setVideoFading(false);
      setVideoHidden(true);
      // Wait 3 seconds before restarting
      setTimeout(() => {
        video.currentTime = 0;
        setVideoFadingIn(true);
        setVideoFading(false);
        setVideoHidden(false);
        video.play();
      }, 3000);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
    };
  }, []);

  const scrollToContact = () => {
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToExpertise = () => {
    document.querySelector('#expertise')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 via-white to-cyan-50">
      {/* Video Background */}
      <div 
        className={`absolute inset-0 w-full h-full overflow-hidden ${
          videoHidden 
            ? 'opacity-0' 
            : videoFadingIn 
            ? 'opacity-100' 
            : videoFading 
            ? 'opacity-0' 
            : 'opacity-100'
        }`}
        style={{
          transition: videoFadingIn 
            ? 'opacity 3000ms cubic-bezier(0.4, 0, 0.2, 1)' 
            : videoFading 
            ? 'opacity 2000ms cubic-bezier(0.4, 0, 0.1, 1)' 
            : videoHidden
            ? 'opacity 0ms'
            : 'none'
        }}
      >
        <video 
          ref={videoRef}
          autoPlay 
          muted 
          playsInline
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full w-auto h-auto object-cover"
          style={{ filter: 'brightness(0.7) contrast(1.1)' }}
        >
          <source 
            src="/videos/tag-animation.mp4"
            type="video/mp4"
          />
        </video>
        {/* Video Overlay for Text Readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/50 to-white/70" />
      </div>
      
      {/* Technical Background Patterns */}
      <div className="absolute inset-0 bg-microchip opacity-10" />
      <div className="absolute inset-0 bg-hex-pattern opacity-15" />
      
      {/* Animated Technical Elements */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-br from-cyan-100/40 to-teal-100/40 rounded-full blur-3xl animate-pulse-slow" />
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-br from-teal-100/30 to-cyan-100/30 rounded-full blur-3xl animate-pulse-slower" />
      
      {/* Content Container */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-32 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-green-50 border border-green-400 rounded-full mb-12 animate-fade-in">
            <Sparkles className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-700">
              Recruiting Excellence by Engineers, for Engineers
            </span>
          </div>

          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light mb-12 animate-fade-in-up leading-tight tracking-tight">
            <span 
              className="bg-gradient-to-r from-cyan-700 via-teal-700 to-cyan-800 bg-clip-text text-transparent transition-opacity duration-300 ease-in-out" 
              style={{ 
                letterSpacing: '0.02em', 
                fontWeight: 300,
                opacity: textFading ? 0 : 1
              }}
            >
              {showRecruiters ? t('hero.expertRecruiters') : t('hero.worldClassTech')}
            </span>
          </h1>

          {/* Tagline */}
          <p className="text-2xl sm:text-3xl font-bold text-slate-800 mb-12 animate-fade-in-up animation-delay-200 tracking-wide">
            {t('hero.searchEnds')}
          </p>

          {/* Description */}
          <p className="text-lg sm:text-xl text-slate-700 max-w-3xl mx-auto mb-10 leading-relaxed animate-fade-in-up animation-delay-300 font-medium">
            {t('hero.description')} <span className="text-cyan-800 font-bold">{t('hero.semiconductors')}</span>, <span className="text-teal-800 font-bold">{t('hero.aiml')}</span>, <span className="text-cyan-800 font-bold">{t('hero.powerElectronics')}</span>, <span className="text-teal-800 font-bold">{t('hero.futureGrid')}</span> {t('hero.technologies')}
          </p>

          {/* Image Carousel */}
          <div className="mb-12 animate-fade-in-up animation-delay-400">
            <div className="max-w-6xl mx-auto overflow-hidden rounded-2xl border-2 border-gray-200 shadow-2xl">
              <div className="flex animate-scroll-carousel">
                {/* First 4 images */}
                <img 
                  src="/images/carousel/mars.jpg" 
                  alt="Mars Helicopter and Rover" 
                  className="w-1/3 object-cover h-64 filter grayscale"
                  style={{ filter: 'grayscale(100%) sepia(20%) hue-rotate(180deg) saturate(0.8)' }}
                  loading="lazy"
                />
                <img 
                  src="/images/carousel/cleanroom.jpg" 
                  alt="Semiconductor Cleanroom" 
                  className="w-1/3 object-cover h-64"
                  style={{ filter: 'grayscale(100%) sepia(20%) hue-rotate(180deg) saturate(0.8)' }}
                  loading="lazy"
                />
                <img 
                  src="/images/carousel/desert.jpg" 
                  alt="Desert Energy Facility" 
                  className="w-1/3 object-cover h-64"
                  style={{ filter: 'grayscale(100%) sepia(20%) hue-rotate(180deg) saturate(0.8)' }}
                  loading="lazy"
                />
                <img 
                  src="/images/carousel/city.jpg" 
                  alt="Smart City Network" 
                  className="w-1/3 object-cover h-64"
                  style={{ filter: 'grayscale(100%) sepia(20%) hue-rotate(180deg) saturate(0.8)' }}
                  loading="lazy"
                />
                {/* Duplicate all 4 for seamless loop */}
                <img 
                  src="/images/carousel/mars.jpg" 
                  alt="Mars Helicopter and Rover" 
                  className="w-1/3 object-cover h-64"
                  style={{ filter: 'grayscale(100%) sepia(20%) hue-rotate(180deg) saturate(0.8)' }}
                  loading="lazy"
                />
                <img 
                  src="/images/carousel/cleanroom.jpg" 
                  alt="Semiconductor Cleanroom" 
                  className="w-1/3 object-cover h-64"
                  style={{ filter: 'grayscale(100%) sepia(20%) hue-rotate(180deg) saturate(0.8)' }}
                  loading="lazy"
                />
                <img 
                  src="/images/carousel/desert.jpg" 
                  alt="Desert Energy Facility" 
                  className="w-1/3 object-cover h-64"
                  style={{ filter: 'grayscale(100%) sepia(20%) hue-rotate(180deg) saturate(0.8)' }}
                  loading="lazy"
                />
                <img 
                  src="/images/carousel/city.jpg" 
                  alt="Smart City Network" 
                  className="w-1/3 object-cover h-64"
                  style={{ filter: 'grayscale(100%) sepia(20%) hue-rotate(180deg) saturate(0.8)' }}
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up animation-delay-400">
            <Button 
              onClick={scrollToContact}
              size="lg"
              className="group bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 text-white font-bold px-8 py-6 text-lg rounded-xl shadow-xl shadow-cyan-500/30 hover:shadow-cyan-600/40 transition-all duration-300"
            >
              {t('hero.hireCTA')}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              onClick={scrollToExpertise}
              size="lg"
              variant="outline"
              className="border-2 border-cyan-600 text-cyan-700 hover:bg-cyan-50 hover:border-cyan-700 font-bold px-8 py-6 text-lg rounded-xl transition-all duration-300"
            >
              {t('hero.viewCTA')}
            </Button>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-[2.6rem] animate-fade-in-up animation-delay-500">
            {[
              { value: '20+', label: t('hero.stat1') },
              { value: '3,500+', label: t('hero.stat2') },
              { value: '98%', label: t('hero.stat3') },
              { value: '< 48hrs', label: t('hero.stat4') }
            ].map((stat, index) => (
              <div key={index} className="group p-6 bg-white/80 backdrop-blur-sm border-2 border-gray-200 rounded-xl hover:border-cyan-500 hover:shadow-xl transition-all duration-300 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative">
                  <div className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-cyan-700 to-teal-700 bg-clip-text text-transparent mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-slate-700 font-semibold group-hover:text-slate-800 transition-colors">
                    {stat.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-cyan-400 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-1.5 bg-cyan-600 rounded-full animate-scroll" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
